// A small, dependency-free markdown parser for wiki pages.
//
// Deliberately not a full CommonMark implementation - it covers the common
// subset a wiki page actually needs (headings, paragraphs, bold/italic,
// inline code, links, images, lists, code blocks, blockquotes, horizontal
// rules, GFM tables, and a [[TOC]] marker) and nothing else. No nested
// emphasis-inside-emphasis, no nested blocks inside list items, no backslash
// escapes, no escaped `|` inside a table cell: known, deliberate scope cuts
// rather than gaps, since this renders untrusted (creator-authored, publicly
// served) content and a bigger surface is a bigger risk surface.
//
// Safety is structural, not a sanitization pass bolted on afterwards: this
// module only ever produces a plain data tree (MarkdownNode), never HTML
// or a DOM string. MarkdownView.tsx turns that tree into React elements
// directly, so there is no dangerouslySetInnerHTML anywhere in the wiki
// rendering path and no raw HTML written by a page author is ever
// interpreted as markup - it renders as literal text, the same as any other
// React text child. The one place user input becomes an attribute rather
// than text is a link/image URL, which isSafeUrl below gates explicitly.

export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'code'; value: string }
  | { type: 'link'; href: string; children: InlineNode[] }
  | { type: 'image'; src: string; alt: string };

export type TableAlign = 'left' | 'center' | 'right' | null;

export type BlockNode =
  | { type: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; children: InlineNode[]; id: string }
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'blockquote'; children: BlockNode[] }
  | { type: 'list'; ordered: boolean; items: InlineNode[][] }
  | { type: 'code_block'; value: string; lang: string | null }
  | { type: 'hr' }
  | { type: 'table'; align: TableAlign[]; header: InlineNode[][]; rows: InlineNode[][][] }
  // Populated after a full parseMarkdown pass with every heading in the
  // document (see the [[TOC]] handling at the end of parseMarkdown below) -
  // only ever direct top-level headings, not ones nested inside a
  // blockquote's own recursively-parsed content.
  | { type: 'toc'; items: { level: 1 | 2 | 3 | 4 | 5 | 6; id: string; text: string }[] };

const SAFE_SCHEME_RE = /^(https?:|mailto:)/i;
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

/**
 * A URL is safe to place in a rendered href/src when it is either a
 * relative/same-site reference (no scheme at all, or an explicit `/`, `#`,
 * `./`, `../` prefix) or uses an explicitly allowlisted scheme (http(s),
 * mailto). Everything else - javascript:, data:, vbscript:, and any
 * obfuscation of those using control characters inside the scheme name
 * (e.g. a literal tab between "java" and "script:") - is rejected. Control
 * characters are stripped before the scheme check specifically to catch
 * that obfuscation; a genuine URL never legitimately contains one.
 */
export function isSafeUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  // eslint-disable-next-line no-control-regex
  const clean = trimmed.replace(/[\x00-\x1f]/g, '');
  if (!clean) return false;
  if (clean.startsWith('/') || clean.startsWith('#') || clean.startsWith('./') || clean.startsWith('../')) {
    return true;
  }
  const scheme = SCHEME_RE.exec(clean);
  if (!scheme) return true; // no scheme at all: a plain relative reference
  return SAFE_SCHEME_RE.test(clean);
}

// The URL segment tolerates one level of balanced parentheses (common in
// real URLs, e.g. a Wikipedia disambiguation link, or - not coincidentally
// - the shape of a javascript: XSS payload like "javascript:alert(1)"):
// a naive "no parens at all" class would stop mid-URL at the first ")" and
// leave the true closing paren stranded as a separate text node instead of
// closing the link syntax cleanly. It is still rejected by isSafeUrl either
// way; this only affects how it degrades, not whether it is treated as safe.
const URL_SEGMENT = '[^\\s()]*(?:\\([^\\s()]*\\)[^\\s()]*)*';
const LINK_RE = new RegExp(`^\\[([^\\]]*)\\]\\((${URL_SEGMENT})\\)$`);
const IMAGE_RE = new RegExp(`^!\\[([^\\]]*)\\]\\((${URL_SEGMENT})\\)$`);

/**
 * Parse inline (span-level) markdown within a single logical line of text.
 *
 * The tokenizer regex is constructed fresh on every call rather than reused
 * from a module-level constant: `parseInline` recurses into itself (bold,
 * italic and link text all re-parse their own inner content), and a shared
 * global-flag RegExp's `lastIndex` is mutable state - a recursive call
 * resuming the *same* regex object mid-scan of the *outer* call's text would
 * silently corrupt the outer loop's position, which manifests as unbounded
 * output rather than a clean crash. A fresh RegExp per call has no state to
 * collide.
 */
export function parseInline(text: string): InlineNode[] {
  const inlineRe = new RegExp(
    '(`[^`]+`)' +
      `|(!\\[[^\\]]*\\]\\((${URL_SEGMENT})\\))` +
      `|(\\[[^\\]]*\\]\\((${URL_SEGMENT})\\))` +
      '|(\\*\\*[^*]+\\*\\*)|(__[^_]+__)|(\\*[^*]+\\*)|(_[^_]+_)',
    'g',
  );
  const nodes: InlineNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = inlineRe.exec(text))) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    const token = match[0];
    if (token.startsWith('`')) {
      nodes.push({ type: 'code', value: token.slice(1, -1) });
    } else if (token.startsWith('![')) {
      const m = IMAGE_RE.exec(token);
      if (m && isSafeUrl(m[2]!)) {
        nodes.push({ type: 'image', alt: m[1]!, src: m[2]! });
      } else {
        nodes.push({ type: 'text', value: token });
      }
    } else if (token.startsWith('[')) {
      const m = LINK_RE.exec(token);
      if (m && isSafeUrl(m[2]!)) {
        nodes.push({ type: 'link', href: m[2]!, children: parseInline(m[1]!) });
      } else {
        nodes.push({ type: 'text', value: token });
      }
    } else if (token.startsWith('**') || token.startsWith('__')) {
      nodes.push({ type: 'strong', children: parseInline(token.slice(2, -2)) });
    } else {
      // Single * or _ emphasis.
      nodes.push({ type: 'em', children: parseInline(token.slice(1, -1)) });
    }
    lastIndex = inlineRe.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return nodes;
}

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const HR_RE = /^(-{3,}|\*{3,}|_{3,})\s*$/;
const FENCE_RE = /^```\s*([^\s`]*)\s*$/;
const QUOTE_RE = /^>\s?(.*)$/;
const UNORDERED_RE = /^[-*]\s+(.*)$/;
const ORDERED_RE = /^\d+\.\s+(.*)$/;
const TOC_RE = /^\[\[TOC\]\]\s*$/i;
const TABLE_SEP_CELL_RE = /^:?-+:?$/;

/** Flatten inline nodes to plain text - used for heading ids and TOC labels,
 * which are plain strings, not rendered markup. */
function inlineToText(nodes: InlineNode[]): string {
  return nodes
    .map((n) => {
      switch (n.type) {
        case 'text':
          return n.value;
        case 'code':
          return n.value;
        case 'image':
          return n.alt;
        case 'strong':
        case 'em':
          return inlineToText(n.children);
        case 'link':
          return inlineToText(n.children);
        default:
          return '';
      }
    })
    .join('');
}

/** A heading id is a plain anchor slug, never interpreted as markup or HTML -
 * React sets it as an ordinary `id` attribute (see MarkdownView.tsx), so
 * arbitrary characters in the source heading text are safe by construction
 * even without this normalisation. It exists to make anchors readable and
 * URL-friendly, not for safety. */
function slugifyHeading(text: string): string {
  return text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
}

/** Split a GFM table row on unescaped `|`, tolerating optional leading/
 * trailing pipes. A `|` inside a link URL or emphasis is not distinguished
 * from a cell separator - a known scope cut (see the module doc). */
function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((c) => c.trim());
}

function tableCellAlign(cell: string): TableAlign {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null;
}

/** True when `line` is a valid GFM alignment row (the required second line of
 * a table, e.g. `| --- | :---: |`) - every cell must be dashes with optional
 * leading/trailing colons, and there must be at least one cell. */
function isTableSeparatorRow(line: string): boolean {
  if (!line.includes('|') && !line.includes('-')) return false;
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((c) => TABLE_SEP_CELL_RE.test(c));
}

/** Parse a full markdown document into a flat list of block nodes. */
export function parseMarkdown(source: string): BlockNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: BlockNode[] = [];
  let i = 0;
  // Heading ids are unique within this call only (not across a recursive
  // blockquote parse, which gets its own counter) - see the `toc` BlockNode
  // doc comment above.
  const seenIds = new Map<string, number>();
  function nextHeadingId(text: string): string {
    const base = slugifyHeading(text);
    const count = seenIds.get(base) ?? 0;
    seenIds.set(base, count + 1);
    return count === 0 ? base : `${base}-${count + 1}`;
  }

  while (i < lines.length) {
    const line = lines[i]!;

    if (line.trim() === '') {
      i++;
      continue;
    }

    const fence = FENCE_RE.exec(line);
    if (fence) {
      const lang = fence[1] || null;
      const body: string[] = [];
      i++;
      while (i < lines.length && lines[i]!.trim() !== '```') {
        body.push(lines[i]!);
        i++;
      }
      i++; // consume the closing fence (or run off the end at EOF)
      blocks.push({ type: 'code_block', value: body.join('\n'), lang });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = heading[1]!.length as 1 | 2 | 3 | 4 | 5 | 6;
      const children = parseInline(heading[2]!);
      blocks.push({ type: 'heading', level, children, id: nextHeadingId(inlineToText(children)) });
      i++;
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }

    if (TOC_RE.test(line)) {
      // Populated in the final pass below, once every heading in this
      // document (including ones after this marker) is known.
      blocks.push({ type: 'toc', items: [] });
      i++;
      continue;
    }

    if (line.includes('|') && i + 1 < lines.length && isTableSeparatorRow(lines[i + 1]!)) {
      const header = splitTableRow(line).map((c) => parseInline(c));
      const align = splitTableRow(lines[i + 1]!).map(tableCellAlign);
      i += 2;
      const rows: InlineNode[][][] = [];
      while (i < lines.length && lines[i]!.trim() !== '' && lines[i]!.includes('|')) {
        rows.push(splitTableRow(lines[i]!).map((c) => parseInline(c)));
        i++;
      }
      blocks.push({ type: 'table', align, header, rows });
      continue;
    }

    const quote = QUOTE_RE.exec(line);
    if (quote) {
      const quoted: string[] = [quote[1]!];
      i++;
      while (i < lines.length) {
        const q = QUOTE_RE.exec(lines[i]!);
        if (!q) break;
        quoted.push(q[1]!);
        i++;
      }
      blocks.push({ type: 'blockquote', children: parseMarkdown(quoted.join('\n')) });
      continue;
    }

    const firstUnordered = UNORDERED_RE.exec(line);
    const firstOrdered = ORDERED_RE.exec(line);
    if (firstUnordered || firstOrdered) {
      const ordered = Boolean(firstOrdered);
      const items: InlineNode[][] = [];
      while (i < lines.length) {
        const l = lines[i]!;
        const m = ordered ? ORDERED_RE.exec(l) : UNORDERED_RE.exec(l);
        if (!m) break;
        items.push(parseInline(m[1]!));
        i++;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    // Plain paragraph: consecutive non-blank, non-block-starting lines join
    // with a space (standard markdown paragraph joining).
    const paragraphLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i]!.trim() !== '' &&
      !FENCE_RE.test(lines[i]!) &&
      !HEADING_RE.test(lines[i]!) &&
      !HR_RE.test(lines[i]!) &&
      !TOC_RE.test(lines[i]!) &&
      !QUOTE_RE.test(lines[i]!) &&
      !UNORDERED_RE.test(lines[i]!) &&
      !ORDERED_RE.test(lines[i]!) &&
      !(lines[i]!.includes('|') && i + 1 < lines.length && isTableSeparatorRow(lines[i + 1]!))
    ) {
      paragraphLines.push(lines[i]!);
      i++;
    }
    blocks.push({ type: 'paragraph', children: parseInline(paragraphLines.join(' ')) });
  }

  const headings = blocks
    .filter((b): b is Extract<BlockNode, { type: 'heading' }> => b.type === 'heading')
    .map((h) => ({ level: h.level, id: h.id, text: inlineToText(h.children) }));
  for (const b of blocks) {
    if (b.type === 'toc') b.items = headings;
  }

  return blocks;
}
